import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Gift, Sparkles } from 'lucide-react';
import { API } from '../config';
import { normalizeInviteCode, storeInviteCode } from '../lib/inviteCode';

const PLAY_BASE = 'https://play.google.com/store/apps/details?id=it.rvincente.app.twa';
const APP_STORE_URL = 'https://apps.apple.com/app/rvincente-pronostici-calcio/id6760314785';

const detectOs = () => {
  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'ios';
  return 'desktop';
};

const playUrl = (code) =>
  `${PLAY_BASE}&referrer=${encodeURIComponent(`utm_source=referral&utm_medium=invite&utm_campaign=invita_amici&ref=${code}`)}`;

export default function InvitePage() {
  const { code: rawCode } = useParams();
  const navigate = useNavigate();
  const code = normalizeInviteCode(rawCode);
  const os = useMemo(detectOs, []);
  const storeUrl = os === 'android' ? playUrl(code) : os === 'ios' ? APP_STORE_URL : null;

  useEffect(() => {
    if (!code) { navigate('/', { replace: true }); return undefined; }
    storeInviteCode(code);
    try {
      navigator.sendBeacon(`${API}/attribution/invite`, JSON.stringify({ code }));
    } catch { /* noop */ }
    // Already inside the app: just go to signup with the code stored.
    if (Capacitor.isNativePlatform()) { navigate('/login?tab=register', { replace: true }); return undefined; }
    if (!storeUrl) return undefined;
    const t = setTimeout(() => { window.location.href = storeUrl; }, 1200);
    return () => clearTimeout(t);
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-[#121212] flex items-center justify-center p-6" data-testid="invite-page">
      <div className="w-full max-w-sm text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mx-auto mb-5">
          <Gift className="w-8 h-8 text-emerald-400" />
        </div>
        <h1 className="text-3xl font-black text-white uppercase mb-2" style={{ fontFamily: 'Barlow Condensed' }}>Un amico ti ha invitato!</h1>
        <p className="text-sm text-gray-400 mb-6">
          Scarica <strong className="text-white">RVINCENTE</strong> gratis: analisi IA su tutte le partite, probabili marcatori e cartellini. Il codice amico <strong className="text-emerald-400" data-testid="invite-code">{code}</strong> si attiva da solo.
        </p>
        {storeUrl ? (
          <a href={storeUrl} className="w-full h-14 rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-black flex items-center justify-center gap-2" data-testid="invite-store-btn">
            <Sparkles className="w-5 h-5" /> SCARICA L'APP GRATIS
          </a>
        ) : (
          <div className="space-y-3">
            <a href={playUrl(code)} className="w-full h-12 rounded-2xl bg-white/10 text-white font-bold flex items-center justify-center" data-testid="invite-play-btn">Google Play</a>
            <a href={APP_STORE_URL} className="w-full h-12 rounded-2xl bg-white/10 text-white font-bold flex items-center justify-center" data-testid="invite-appstore-btn">App Store</a>
          </div>
        )}
        <button onClick={() => navigate('/login?tab=register')} className="mt-4 text-xs text-gray-500 underline" data-testid="invite-web-btn">
          Oppure registrati dal browser
        </button>
      </div>
    </div>
  );
}
